import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { requestDownload } from '../api/client';
import { Button } from './Button';
import s from './PdfPreview.module.css';

interface PdfPreviewProps {
  src: string;
  title: string;
  downloadHref?: string;
  downloadLabel?: string;
  downloadName?: string;
  /** False: no download link (APP-130: an entry sample is for looking at, not keeping). */
  download?: boolean;
  /** What a reader is told when the PDF can't be drawn; by default, to use the download link. */
  errorText?: string;
  onReady?: () => void;
  onError?: () => void;
}

/** Render the actual server PDF without depending on a browser's native PDF plug-in. */
export function PdfPreview(props: PdfPreviewProps) {
  // A new document cannot inherit an old page, canvas or pending loading task.
  return <DocumentPreview key={props.src} {...props} />;
}

const PDF_ERROR = 'This PDF could not be displayed. Use the download link to review it.';

function PdfError({ text = PDF_ERROR }: { text?: string | undefined }) {
  return <p role="alert">{text}</p>;
}

function DocumentPreview({
  src,
  title,
  downloadHref = src,
  downloadLabel = 'Download PDF',
  downloadName,
  download = true,
  errorText,
  onReady,
  onError,
}: PdfPreviewProps) {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(600);
  const viewport = useRef<HTMLDivElement>(null);
  // A named scroll region is keyboard focusable, as in useScrollRegion.
  const scrollRegion = { role: 'region', 'aria-label': `${title} pages`, tabIndex: 0 } as const;
  const reportError = useEffectEvent(() => {
    onError?.();
  });

  useEffect(() => {
    const controller = new AbortController();
    let loading: PDFDocumentLoadingTask | undefined;
    void (async () => {
      try {
        const file = await requestDownload(src, {
          contentType: 'application/pdf',
          maxBytes: 50 * 1024 * 1024,
          signal: controller.signal,
        });
        const bytes = await file.arrayBuffer();
        controller.signal.throwIfAborted();
        const pdfjs = await import('pdfjs-dist');
        controller.signal.throwIfAborted();
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        loading = pdfjs.getDocument({
          data: new Uint8Array(bytes),
          // Draw the PDF's embedded glyph outlines. CSP allows neither blob fonts nor eval/Wasm.
          disableFontFace: true,
          useWasm: false,
        });
        const loaded = await loading.promise;
        if (!controller.signal.aborted) setDocument(loaded);
      } catch {
        if (!controller.signal.aborted) {
          setFailed(true);
          reportError();
        }
      }
    })();
    return () => {
      controller.abort();
      void loading?.destroy().catch(() => undefined);
    };
  }, [src]);

  useEffect(() => {
    const element = viewport.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0) setWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section className={s.viewer} aria-label={title}>
      <div className={s.toolbar}>
        {document && (
          <>
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page === 1}
              onPress={() => {
                setPage(page - 1);
                if (viewport.current) viewport.current.scrollTop = 0;
              }}
            >
              Previous page
            </Button>
            <span role="status" aria-live="polite">
              Page {page} of {document.numPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              isDisabled={page === document.numPages}
              onPress={() => {
                setPage(page + 1);
                if (viewport.current) viewport.current.scrollTop = 0;
              }}
            >
              Next page
            </Button>
          </>
        )}
        {download && (
          <a href={downloadHref} download={downloadName ?? true}>
            {downloadLabel}
          </a>
        )}
      </div>
      <div ref={viewport} className={s.viewport} {...scrollRegion}>
        {failed ? (
          <PdfError text={errorText} />
        ) : document ? (
          // Keyed by page only: a new width redraws the page in place (QA-068).
          <PdfPage
            key={page}
            document={document}
            number={page}
            width={width}
            errorText={errorText}
            {...(onReady && { onReady })}
            {...(onError && { onError })}
          />
        ) : (
          <p role="status">Loading PDF…</p>
        )}
      </div>
    </section>
  );
}

function PdfPage({
  document,
  number,
  width,
  errorText,
  onReady,
  onError,
}: {
  document: PDFDocumentProxy;
  number: number;
  width: number;
  errorText?: string | undefined;
  onReady?: () => void;
  onError?: () => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [text, setText] = useState('');
  const reportReady = useEffectEvent(() => {
    onReady?.();
  });
  const reportError = useEffectEvent(() => {
    onError?.();
  });
  useEffect(() => {
    const lifecycle = new AbortController();
    let rendering: RenderTask | undefined;
    void (async () => {
      try {
        const page = await document.getPage(number);
        const target = canvas.current;
        if (lifecycle.signal.aborted || !target) return;
        const base = page.getViewport({ scale: 1 });
        const view = page.getViewport({ scale: width / base.width });
        const density = Math.min(window.devicePixelRatio || 1, 2);
        // Draw off screen and copy the finished frame, so a redraw at a new width never blanks
        // or collapses the page on screen (QA-068).
        const frame = target.ownerDocument.createElement('canvas');
        frame.width = Math.ceil(view.width * density);
        frame.height = Math.ceil(view.height * density);
        rendering = page.render({
          canvas: frame,
          viewport: view,
          ...(density !== 1 && { transform: [density, 0, 0, density, 0, 0] }),
        });
        await rendering.promise;
        lifecycle.signal.throwIfAborted();
        target.width = frame.width;
        target.height = frame.height;
        target.getContext('2d')?.drawImage(frame, 0, 0);
        const content = await page.getTextContent();
        lifecycle.signal.throwIfAborted();
        setText(content.items.flatMap((item) => ('str' in item ? [item.str] : [])).join(' '));
        setStatus('ready');
        if (number === 1) reportReady();
      } catch {
        if (!lifecycle.signal.aborted) {
          setStatus('failed');
          reportError();
        }
      }
    })();
    return () => {
      lifecycle.abort();
      rendering?.cancel();
    };
  }, [document, number, width]);
  return (
    <>
      {status === 'loading' && <p role="status">Loading page {number}…</p>}
      {status === 'failed' && <PdfError text={errorText} />}
      <canvas ref={canvas} className={s.canvas} hidden={status !== 'ready'} aria-hidden="true" />
      {status === 'ready' && <p className="visually-hidden">{text}</p>}
    </>
  );
}
